import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  ClipboardCheck, Plus, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, Minus, Users, FileText, Calendar, Target,
  Loader2, Activity, Shield, Clock, Upload, Sparkles, Edit2, Save, X, UserPlus,
  Brain, Lightbulb, AlertCircle, Eye, ChevronRight, ArrowRight, Trash2,
  FileCheck, Stethoscope, HeartHandshake, Bell
} from 'lucide-react';
import AIContent from '@/components/AIContent';
import { ExportDropdown } from '@/components/ExportDropdown';
import { AIExportDropdown } from '@/components/AIExportDropdown';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';
import { DataHistoryPanel } from '@/components/DataHistory';
import { EnhancedReportSection } from '@/components/EnhancedReportSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// KPI Definitions with targets and categories
const KPI_DEFINITIONS = [
  { 
    id: 'initial_poc', 
    name: 'Initial Plan of Care', 
    category: 'Plan of Care',
    target: 0, 
    targetLabel: '0 missing',
    description: 'POC completed within 5 days of admission',
    priority: 'high'
  },
  { 
    id: 'updated_poc', 
    name: 'Updated POC for IDT', 
    category: 'Plan of Care',
    target: 0, 
    targetLabel: '0 missing',
    description: 'POC updated before each IDT meeting',
    priority: 'high'
  },
  { 
    id: 'idg_assessment', 
    name: 'IDG Comprehensive Assessment', 
    category: 'Plan of Care',
    target: 0, 
    targetLabel: '0 missing',
    description: 'Comprehensive assessment completed every 14 days',
    priority: 'high'
  },
  { 
    id: 'scheduling_visits', 
    name: 'Scheduling Visits in Wellsky', 
    category: 'Scheduling',
    target: 0, 
    targetLabel: '0 missing',
    description: 'All visits properly scheduled in Wellsky',
    priority: 'medium'
  },
  { 
    id: 'rncm_visits', 
    name: 'RNCM Missing Visits', 
    category: 'Visits',
    target: 0, 
    targetLabel: '0 missing',
    description: 'RN Case Manager visits completed as scheduled',
    priority: 'high'
  },
  { 
    id: 'aide_visits', 
    name: 'Aide Missing Visits', 
    category: 'Visits',
    target: 0, 
    targetLabel: '0 missing',
    description: 'Aide visits completed as scheduled',
    priority: 'medium'
  },
  { 
    id: 'aide_supervisory', 
    name: 'Aide Supervisory Visits', 
    category: 'Visits',
    target: 0, 
    targetLabel: '0 missing',
    description: 'RN supervisory visits for aides completed every 14 days per CMS requirement',
    priority: 'high'
  },
  { 
    id: 'volunteer_visits', 
    name: 'Volunteer Missing Visits', 
    category: 'Visits',
    target: 0, 
    targetLabel: '0 missing',
    description: 'Volunteer visits completed per patient preference',
    priority: 'low'
  },
  { 
    id: 'triage_risk', 
    name: 'Triage Risk Priority', 
    category: 'Clinical',
    target: 0, 
    targetLabel: '0 missing',
    description: 'Triage risk assessments documented timely',
    priority: 'high'
  },
  { 
    id: 'dme_orders', 
    name: 'DME Orders Documentation', 
    category: 'Clinical',
    target: 0, 
    targetLabel: '0 missing',
    description: 'DME orders properly documented and signed',
    priority: 'medium'
  },
];

// Follow-up roles
const FOLLOW_UP_ROLES = [
  'Blue Team Manager',
  'Yellow Team Manager', 
  'Green Team Manager',
  'Scheduler',
  'Volunteer Manager',
  'Quality RN',
  'Director of Admissions',
  'Psychosocial Lead',
];

// Status calculation - now based on percentage
const getKPIStatus = (percentage) => {
  if (percentage >= 95) return { status: 'Met', color: 'bg-emerald-500', textColor: 'text-emerald-600' };
  if (percentage >= 90) return { status: 'Monitor', color: 'bg-amber-500', textColor: 'text-amber-600' };
  return { status: 'Action Required', color: 'bg-red-500', textColor: 'text-red-600' };
};

// Calculate compliance percentage
const calculatePercentage = (missing, total) => {
  if (!total || total <= 0) return 100;
  const compliant = Math.max(0, total - missing);
  return Math.round((compliant / total) * 1000) / 10; // One decimal place
};

export default function ComplianceDashboardPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpiData, setKpiData] = useState(null);
  const [weeklyHistory, setWeeklyHistory] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [predictions, setPredictions] = useState(null);
  
  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Regulatory compliance data
  const [regulatoryData, setRegulatoryData] = useState(null);
  const [regulatoryLoading, setRegulatoryLoading] = useState(false);

  // Week selection for audits
  const [selectedWeekDate, setSelectedWeekDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState("kpis");

  // Get week info from a date
  const getWeekFromDate = (dateStr) => {
    const date = new Date(dateStr);
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return {
      start: startOfWeek.toISOString().split('T')[0],
      end: endOfWeek.toISOString().split('T')[0],
      label: `Week of ${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    };
  };

  // Get current week info
  const getCurrentWeek = () => {
    return getWeekFromDate(new Date().toISOString().split('T')[0]);
  };

  const selectedWeek = getWeekFromDate(selectedWeekDate);
  const [currentWeek] = useState(getCurrentWeek());

  // Navigate weeks
  const navigateWeek = (direction) => {
    const current = new Date(selectedWeekDate);
    current.setDate(current.getDate() + (direction * 7));
    setSelectedWeekDate(current.toISOString().split('T')[0]);
  };

  // Go to current week
  const goToCurrentWeek = () => {
    setSelectedWeekDate(new Date().toISOString().split('T')[0]);
  };

  useEffect(() => {
    fetchData();
    fetchRegulatoryData();
  }, [token, selectedWeekDate]);

  const fetchData = async () => {
    try {
      const weekInfo = getWeekFromDate(selectedWeekDate);
      
      const [weekRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/compliance/dashboard/by-week?week_start=${weekInfo.start}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/compliance/dashboard?limit=12`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (weekRes.ok) {
        const data = await weekRes.json();
        if (!data.message && data.id) {
          setKpiData(data);
          initializeEditData(data);
        } else {
          // No data for this week - show empty state
          setKpiData(null);
          initializeEditData({});
        }
      } else {
        // Try latest as fallback for current week
        const latestRes = await fetch(`${API_URL}/api/compliance/dashboard/latest`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (latestRes.ok) {
          const data = await latestRes.json();
          if (!data.message) {
            setKpiData(data);
            initializeEditData(data);
          }
        }
      }
      
      if (historyRes.ok) {
        const history = await historyRes.json();
        setWeeklyHistory(history);
        generatePredictions(history);
      }
    } catch (error) {
      toast.error('Failed to load KPI data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchRegulatoryData = async () => {
    setRegulatoryLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/compliance/regulatory/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRegulatoryData(data);
      }
    } catch (error) {
      console.error('Error fetching regulatory data:', error);
    } finally {
      setRegulatoryLoading(false);
    }
  };

  const initializeEditData = (data) => {
    const edit = {};
    KPI_DEFINITIONS.forEach(kpi => {
      const fieldName = `${kpi.id}_missing`;
      const followupField = `${kpi.id}_followup`;
      edit[fieldName] = data[fieldName] || 0;
      edit[followupField] = data[followupField] || data[`${kpi.id}_followup_person`] || '';
    });
    edit.overall_compliance_rating = data.overall_compliance_rating || 0;
    edit.total_count = data.total_count || 0;
    setEditData(edit);
  };

  const generatePredictions = (history) => {
    if (history.length < 3) {
      setPredictions(null);
      return;
    }

    // Calculate trends for each KPI
    const predictions = {};
    KPI_DEFINITIONS.forEach(kpi => {
      const fieldName = `${kpi.id}_missing`;
      const values = history.slice(0, 4).map(h => h[fieldName] || 0).reverse();
      
      if (values.length >= 2) {
        const trend = values[values.length - 1] - values[0];
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        const predicted = Math.max(0, Math.round(values[values.length - 1] + (trend / values.length)));
        
        predictions[kpi.id] = {
          current: values[values.length - 1],
          predicted: predicted,
          trend: trend > 0 ? 'increasing' : trend < 0 ? 'decreasing' : 'stable',
          trendValue: trend,
          average: Math.round(avg * 10) / 10,
          risk: predicted > 2 ? 'high' : predicted > 0 ? 'medium' : 'low'
        };
      }
    });
    
    setPredictions(predictions);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Validate total count
      const totalCount = editData.total_count || 0;
      if (totalCount <= 0) {
        toast.error('Please enter a Total Count (base denominator) greater than 0');
        setSaving(false);
        return;
      }

      // Calculate overall compliance based on percentages
      let totalPercentage = 0;
      let kpiCount = 0;
      
      // Map edit data to backend format
      const saveData = {
        report_date: selectedWeek.start,
        week_start: selectedWeek.start,
        week_end: selectedWeek.end,
        week_label: selectedWeek.label,
        total_count: totalCount,
      };
      
      KPI_DEFINITIONS.forEach(kpi => {
        const fieldName = `${kpi.id}_missing`;
        const followupField = `${kpi.id}_followup`;
        const missing = editData[fieldName] || 0;
        const pct = calculatePercentage(missing, totalCount);
        totalPercentage += pct;
        kpiCount++;
        
        // Map to backend field names
        if (kpi.id === 'rncm_visits') {
          saveData.rncm_missing_visits = missing;
          saveData.rncm_followup_person = editData[followupField] || '';
        } else if (kpi.id === 'aide_visits') {
          saveData.aide_missing_visits = missing;
          saveData.aide_followup_person = editData[followupField] || '';
        } else if (kpi.id === 'aide_supervisory') {
          saveData.aide_supervisory_missing = missing;
          saveData.aide_supervisory_followup_person = editData[followupField] || '';
        } else if (kpi.id === 'volunteer_visits') {
          saveData.volunteer_missing_visits = missing;
          saveData.volunteer_followup_person = editData[followupField] || '';
        } else {
          saveData[fieldName] = missing;
          saveData[followupField] = editData[followupField] || '';
        }
      });

      // Calculate overall compliance rating as average of all KPI percentages
      saveData.overall_compliance_rating = kpiCount > 0 ? Math.round((totalPercentage / kpiCount) * 10) / 10 : 100;

      const response = await fetch(`${API_URL}/api/compliance/dashboard`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        toast.success(`Weekly KPIs saved for ${selectedWeek.label}`);
        setEditMode(false);
        fetchData();
        
        // Auto-trigger AI analysis after saving
        toast.info('Generating AI analysis...');
        fetchAIInsights();
      } else {
        toast.error('Failed to save KPIs');
      }
    } catch (error) {
      toast.error('Error saving data');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      toast.error('Please paste data to import');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('text', importText);
      formData.append('report_date', currentWeek.start);

      const response = await fetch(`${API_URL}/api/compliance/import-text`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Imported ${Object.keys(result.parsed_data).length - 5} fields`);
        setShowImportDialog(false);
        setImportText('');
        fetchData();
      } else {
        toast.error('Failed to parse import data');
      }
    } catch (error) {
      toast.error('Import error');
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteAudit = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/compliance/dashboard/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const displayLabel = deleteTarget.week_label || deleteTarget.report_date || 
          (deleteTarget.created_at ? new Date(deleteTarget.created_at).toLocaleDateString() : 'Entry');
        toast.success(`Audit entry deleted: ${displayLabel}`);
        setShowDeleteDialog(false);
        setDeleteTarget(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete audit entry');
      }
    } catch (error) {
      toast.error('Error deleting audit entry');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (audit) => {
    setDeleteTarget(audit);
    setShowDeleteDialog(true);
  };

  const fetchAIInsights = async () => {
    setLoadingAI(true);
    try {
      const response = await fetch(`${API_URL}/api/compliance/ai-insights`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        }
      });
      if (response.ok) {
        setAiInsights(await response.json());
      }
    } catch (error) {
      toast.error('Failed to generate insights');
    } finally {
      setLoadingAI(false);
    }
  };

  // Calculate summary stats based on percentages
  const calculateSummary = () => {
    if (!kpiData) return { met: 0, monitor: 0, action: 0, total: KPI_DEFINITIONS.length };
    
    const totalCount = kpiData.total_count || 0;
    let met = 0, monitor = 0, action = 0;
    
    KPI_DEFINITIONS.forEach(kpi => {
      const fieldName = `${kpi.id}_missing`;
      let value = 0;
      
      if (kpi.id === 'rncm_visits') {
        value = kpiData.rncm_missing_visits || 0;
      } else if (kpi.id === 'aide_visits') {
        value = kpiData.aide_missing_visits || 0;
      } else if (kpi.id === 'volunteer_visits') {
        value = kpiData.volunteer_missing_visits || 0;
      } else {
        value = kpiData[fieldName] || 0;
      }
      
      const percentage = calculatePercentage(value, totalCount);
      const status = getKPIStatus(percentage);
      
      if (status.status === 'Met') met++;
      else if (status.status === 'Monitor') monitor++;
      else action++;
    });
    
    return { met, monitor, action, total: KPI_DEFINITIONS.length };
  };

  const summary = calculateSummary();
  const complianceScore = Math.round((summary.met / summary.total) * 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-kpi-dashboard">
      {/* Modern Header */}
      <PageHeader
        icon={ClipboardCheck}
        title="Compliance KPI Dashboard"
        subtitle="Weekly Audit & Performance Tracking"
        gradient="from-emerald-600 via-teal-600 to-cyan-600"
      >
        <div className="flex flex-wrap items-center gap-2">
          <ExportDropdown reportType="compliance" token={token} />
          <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" size="sm" onClick={() => { setRefreshing(true); fetchData(); }}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" size="sm" onClick={() => setShowImportDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          {editMode ? (
            <>
              <Button variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-0" size="sm" onClick={() => setEditMode(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-white text-emerald-600 hover:bg-white/90">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Week
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => setEditMode(true)} className="bg-white text-emerald-600 hover:bg-white/90">
              <Edit2 className="h-4 w-4 mr-2" />
              Update KPIs
            </Button>
          )}
        </div>
      </PageHeader>

      {/* Week Selector */}
      <ModernCard className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-emerald-500" />
              <span className="font-medium">Audit Week:</span>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateWeek(-1)}
                data-testid="prev-week-btn"
              >
                <ChevronRight className="h-4 w-4 rotate-180" />
                Previous
              </Button>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border min-w-[280px] justify-center">
                <Input
                  type="date"
                  value={selectedWeekDate}
                  onChange={(e) => setSelectedWeekDate(e.target.value)}
                  className="w-[140px] border-0 p-0 h-auto text-center"
                  data-testid="week-date-picker"
                />
                <Separator orientation="vertical" className="h-5" />
                <span className="text-sm font-medium text-[#00b0ab]" data-testid="selected-week-label">
                  {selectedWeek.label}
                </span>
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateWeek(1)}
                disabled={new Date(selectedWeekDate) >= new Date()}
                data-testid="next-week-btn"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              
              {selectedWeek.start !== currentWeek.start && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={goToCurrentWeek}
                  className="text-[#00b0ab]"
                  data-testid="today-btn"
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Today
                </Button>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {selectedWeek.start} to {selectedWeek.end}
            </div>
          </div>
        </CardContent>
      </ModernCard>

      {/* No Data State - hide when in edit mode */}
      {!kpiData && !editMode && (
        <Card className="border-dashed border-2">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Audit Data for This Week</h3>
            <p className="text-muted-foreground mb-4">
              No compliance data has been entered for {selectedWeek.label}.
            </p>
            <Button onClick={() => { 
              setEditMode(true); 
              setActiveTab("kpis");
              // Initialize empty edit data for new entry
              initializeEditData({});
            }} data-testid="enter-audit-data-btn">
              <Plus className="h-4 w-4 mr-2" />
              Enter Audit Data
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Edit Mode Banner when entering new data */}
      {!kpiData && editMode && (
        <Card className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Edit2 className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-800 dark:text-emerald-200">Entering New Audit Data</p>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">Fill in the KPI fields below for {selectedWeek.label}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save Data
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {kpiData && (
        <>
        {/* Main Score Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500/10 to-violet-500/10 border-[#00b0ab]/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Overall Score</p>
            <p className="text-3xl font-bold text-[#00b0ab]">{kpiData?.overall_compliance_rating || complianceScore}%</p>
            <Progress value={kpiData?.overall_compliance_rating || complianceScore} className="mt-2 h-2" />
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-200">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">KPIs Met (≥95%)</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold text-emerald-600">{summary.met}</p>
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
            </div>
            <p className="text-xs text-emerald-600 mt-1">of {summary.total} total KPIs</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Monitoring (90-94%)</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold text-amber-600">{summary.monitor}</p>
              <Eye className="h-6 w-6 text-amber-500" />
            </div>
            <p className="text-xs text-amber-600 mt-1">needs attention</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500/10 to-rose-500/10 border-red-200">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Action Required (&lt;90%)</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold text-red-600">{summary.action}</p>
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <p className="text-xs text-red-600 mt-1">immediate follow-up</p>
          </CardContent>
        </Card>
      </div>

      {/* All KPI Scores Summary Grid */}
      <Card className="mt-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-[#00b0ab]" />
            All KPI Compliance Scores
          </CardTitle>
          <CardDescription>
            {kpiData?.total_count > 0 
              ? `Based on ${kpiData.total_count} total items | ${kpiData?.week_label || kpiData?.report_date || 'Current Week'}`
              : 'Enter Total Count to calculate compliance percentages'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {KPI_DEFINITIONS.map(kpi => {
              let missing = 0;
              if (kpi.id === 'rncm_visits') missing = kpiData?.rncm_missing_visits || 0;
              else if (kpi.id === 'aide_visits') missing = kpiData?.aide_missing_visits || 0;
              else if (kpi.id === 'volunteer_visits') missing = kpiData?.volunteer_missing_visits || 0;
              else missing = kpiData?.[`${kpi.id}_missing`] || 0;
              
              const totalCount = kpiData?.total_count || 0;
              const percentage = calculatePercentage(missing, totalCount);
              const status = getKPIStatus(percentage);
              
              return (
                <div 
                  key={kpi.id}
                  className={`p-3 rounded-lg border text-center ${
                    status.status === 'Met' ? 'bg-emerald-50 border-emerald-200' : 
                    status.status === 'Monitor' ? 'bg-amber-50 border-amber-200' : 
                    'bg-red-50 border-red-200'
                  }`}
                >
                  <p className="text-xs font-medium text-muted-foreground truncate" title={kpi.name}>
                    {kpi.name.replace('Missing ', '').replace(' Visits', '')}
                  </p>
                  <p className={`text-2xl font-bold ${status.textColor}`}>
                    {totalCount > 0 ? `${percentage}%` : '-'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {missing} missing
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      </>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="report" className="gap-2">
            <FileText className="h-4 w-4" /> Report
          </TabsTrigger>
          <TabsTrigger value="kpis" className="gap-2">
            <Target className="h-4 w-4" /> Weekly KPIs
          </TabsTrigger>
          <TabsTrigger value="regulatory" className="gap-2">
            <Shield className="h-4 w-4" /> Regulatory
          </TabsTrigger>
          <TabsTrigger value="action" className="gap-2">
            <AlertTriangle className="h-4 w-4" /> Action Items
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2">
            <Brain className="h-4 w-4" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2">
            <TrendingUp className="h-4 w-4" /> Trends
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <Clock className="h-4 w-4" /> History
          </TabsTrigger>
        </TabsList>

        {/* Enhanced Report Tab */}
        <TabsContent value="report">
          <EnhancedReportSection 
            domain="compliance"
            title="Compliance Report"
            description="Regulatory compliance metrics, CTI/F2F tracking, and audit readiness with AI insights"
            defaultTimeframe="30d"
          />
        </TabsContent>

        {/* Weekly KPIs Tab */}
        <TabsContent value="kpis">
          <Card>
            <CardHeader>
              <CardTitle>Weekly KPI Status</CardTitle>
              <CardDescription>
                Enter missing counts and Total Count to calculate compliance percentages automatically
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Total Count Input - Required for percentage calculation */}
              {editMode && (
                <div className="mb-6 p-4 bg-[#00b0ab]/10 dark:bg-[#00b0ab]/10/30 rounded-lg border border-[#00b0ab]/30">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-indigo-700">Total Count (Base Denominator)</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        This is the total number of items/patients/visits to measure compliance against. 
                        All KPI percentages are calculated as: (Total - Missing) / Total × 100
                      </p>
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        min="1"
                        value={editData.total_count || ''}
                        onChange={(e) => setEditData({...editData, total_count: parseInt(e.target.value) || 0})}
                        placeholder="e.g., 50"
                        className="h-10 text-lg font-semibold text-center"
                        data-testid="total-count-input"
                      />
                    </div>
                  </div>
                  {editData.total_count > 0 && (
                    <div className="mt-3 text-sm text-[#00b0ab]">
                      With {editData.total_count} total items: 1 missing = {calculatePercentage(1, editData.total_count)}% compliance
                    </div>
                  )}
                </div>
              )}
              
              {/* Display Total Count when not editing */}
              {!editMode && kpiData?.total_count > 0 && (
                <div className="mb-6 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Base Denominator (Total Count):</span>
                  <span className="font-semibold text-lg">{kpiData.total_count}</span>
                </div>
              )}
              
              <div className="space-y-1">
                {/* Group by category */}
                {['Plan of Care', 'Visits', 'Scheduling', 'Clinical'].map(category => (
                  <div key={category} className="mb-6">
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {KPI_DEFINITIONS.filter(k => k.category === category).map(kpi => {
                        const fieldName = `${kpi.id}_missing`;
                        const followupField = `${kpi.id}_followup`;
                        
                        // Get value from kpiData with fallback for different field names
                        let currentValue = 0;
                        if (kpiData) {
                          if (kpi.id === 'rncm_visits') {
                            currentValue = kpiData.rncm_missing_visits || 0;
                          } else if (kpi.id === 'aide_visits') {
                            currentValue = kpiData.aide_missing_visits || 0;
                          } else if (kpi.id === 'volunteer_visits') {
                            currentValue = kpiData.volunteer_missing_visits || 0;
                          } else {
                            currentValue = kpiData[fieldName] || 0;
                          }
                        }
                        
                        const followupValue = kpiData?.[followupField] || kpiData?.[`${kpi.id}_followup_person`] || '';
                        
                        // Calculate percentage for status
                        const totalCount = editMode ? (editData.total_count || 0) : (kpiData?.total_count || 0);
                        const missingForCalc = editMode ? (editData[fieldName] || 0) : currentValue;
                        const percentage = calculatePercentage(missingForCalc, totalCount);
                        const status = getKPIStatus(percentage);
                        const prediction = predictions?.[kpi.id];

                        return (
                          <div 
                            key={kpi.id}
                            className={`p-4 rounded-lg border ${status.status === 'Met' ? 'bg-emerald-50/50 border-emerald-200' : status.status === 'Monitor' ? 'bg-amber-50/50 border-amber-200' : 'bg-red-50/50 border-red-200'}`}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{kpi.name}</h4>
                                  <Badge className={status.color}>{status.status}</Badge>
                                  {kpi.priority === 'high' && (
                                    <Badge variant="outline" className="text-red-600 border-red-300">High Priority</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{kpi.description}</p>
                              </div>

                              {editMode ? (
                                <div className="flex items-center gap-3">
                                  <div className="w-20">
                                    <Label className="text-xs">Missing</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      max={editData.total_count || 9999}
                                      value={editData[fieldName] || 0}
                                      onChange={(e) => setEditData({...editData, [fieldName]: parseInt(e.target.value) || 0})}
                                      className="h-8"
                                    />
                                  </div>
                                  {editData.total_count > 0 && (
                                    <div className="w-16 text-center">
                                      <Label className="text-xs">Compliance</Label>
                                      <p className={`text-lg font-bold ${status.textColor}`}>
                                        {percentage}%
                                      </p>
                                    </div>
                                  )}
                                  <div className="w-48">
                                    <Label className="text-xs">Follow-up</Label>
                                    <Select 
                                      value={editData[followupField] || 'none'} 
                                      onValueChange={(v) => setEditData({...editData, [followupField]: v === 'none' ? '' : v})}
                                    >
                                      <SelectTrigger className="h-8">
                                        <SelectValue placeholder="Assign..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">None</SelectItem>
                                        {FOLLOW_UP_ROLES.map(role => (
                                          <SelectItem key={role} value={role}>{role}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-4">
                                  {/* Show percentage as primary display */}
                                  <div className="text-right">
                                    <p className={`text-2xl font-bold ${status.textColor}`}>
                                      {totalCount > 0 ? `${percentage}%` : currentValue}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {totalCount > 0 ? `${currentValue} missing of ${totalCount}` : 'missing'}
                                    </p>
                                  </div>
                                  {followupValue && (
                                    <div className="flex items-center gap-1 text-sm bg-white/50 px-2 py-1 rounded">
                                      <UserPlus className="h-3 w-3" />
                                      <span className="truncate max-w-[120px]">{followupValue}</span>
                                    </div>
                                  )}
                                  {prediction && prediction.trend !== 'stable' && (
                                    <div className={`flex items-center gap-1 text-xs ${prediction.trend === 'decreasing' ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {prediction.trend === 'decreasing' ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                                      <span>{prediction.trend === 'decreasing' ? 'Improving' : 'Worsening'}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Regulatory Compliance Tab */}
        <TabsContent value="regulatory">
          <div className="space-y-4">
            {/* Overall Regulatory Status */}
            {regulatoryData && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className={`border-2 ${
                  regulatoryData.status?.color === 'green' ? 'border-emerald-200 bg-emerald-50' :
                  regulatoryData.status?.color === 'yellow' ? 'border-amber-200 bg-amber-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Overall Regulatory Score</p>
                        <p className="text-3xl font-bold">{regulatoryData.overall_score}%</p>
                        <Badge className={
                          regulatoryData.status?.color === 'green' ? 'bg-emerald-100 text-emerald-700' :
                          regulatoryData.status?.color === 'yellow' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {regulatoryData.status?.level}
                        </Badge>
                      </div>
                      <Shield className={`h-10 w-10 ${
                        regulatoryData.status?.color === 'green' ? 'text-emerald-500' :
                        regulatoryData.status?.color === 'yellow' ? 'text-amber-500' : 'text-red-500'
                      }`} />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">CTI Compliance</p>
                        <p className="text-3xl font-bold">{regulatoryData.metrics?.cti?.compliance_pct || 100}%</p>
                        <p className="text-xs text-slate-400">100% required before billing</p>
                      </div>
                      <FileCheck className={`h-10 w-10 ${
                        regulatoryData.metrics?.cti?.compliance_pct === 100 ? 'text-emerald-500' : 'text-red-500'
                      }`} />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">F2F Compliance</p>
                        <p className="text-3xl font-bold">{regulatoryData.metrics?.f2f?.compliance_pct || 100}%</p>
                        <p className="text-xs text-slate-400">Before day 180 recert</p>
                      </div>
                      <Stethoscope className={`h-10 w-10 ${
                        regulatoryData.metrics?.f2f?.compliance_pct === 100 ? 'text-emerald-500' : 'text-amber-500'
                      }`} />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">POC Updates</p>
                        <p className="text-3xl font-bold">{regulatoryData.metrics?.poc?.compliance_pct || 100}%</p>
                        <p className="text-xs text-slate-400">Updated every 15 days</p>
                      </div>
                      <FileText className={`h-10 w-10 ${
                        regulatoryData.metrics?.poc?.compliance_pct === 100 ? 'text-emerald-500' : 'text-amber-500'
                      }`} />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Billing Hold Alert */}
            {regulatoryData?.billing_status?.hold_count > 0 && (
              <Card className="border-red-300 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                    <div>
                      <p className="font-bold text-red-800">BILLING HOLD ACTIVE</p>
                      <p className="text-sm text-red-600">
                        {regulatoryData.billing_status.hold_count} patient(s) blocked from billing due to missing compliance items
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Detailed Metrics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#00b0ab]" />
                  CHAP/CMS Regulatory Metrics
                </CardTitle>
                <CardDescription>
                  Critical compliance items required by CHAP accreditation and CMS Conditions of Participation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* CTI */}
                  <div className={`p-4 rounded-lg border ${
                    regulatoryData?.metrics?.cti?.missing > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <FileCheck className="h-5 w-5" />
                      <h4 className="font-semibold">CTI Documentation</h4>
                    </div>
                    <p className="text-2xl font-bold">{regulatoryData?.metrics?.cti?.compliance_pct || 100}%</p>
                    <p className="text-sm text-slate-600">
                      {regulatoryData?.metrics?.cti?.missing || 0} missing of {regulatoryData?.metrics?.cti?.total || 0}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {regulatoryData?.metrics?.cti?.description}
                    </p>
                    {regulatoryData?.metrics?.cti?.missing > 0 && (
                      <Badge className="mt-2 bg-red-100 text-red-700">BILLING BLOCKED</Badge>
                    )}
                  </div>

                  {/* F2F */}
                  <div className={`p-4 rounded-lg border ${
                    regulatoryData?.metrics?.f2f?.overdue > 0 ? 'bg-red-50 border-red-200' : 
                    regulatoryData?.metrics?.f2f?.pending > 0 ? 'bg-amber-50 border-amber-200' : 
                    'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Stethoscope className="h-5 w-5" />
                      <h4 className="font-semibold">Face-to-Face Encounter</h4>
                    </div>
                    <p className="text-2xl font-bold">{regulatoryData?.metrics?.f2f?.compliance_pct || 100}%</p>
                    <p className="text-sm text-slate-600">
                      {regulatoryData?.metrics?.f2f?.overdue || 0} overdue, {regulatoryData?.metrics?.f2f?.pending || 0} pending
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {regulatoryData?.metrics?.f2f?.description}
                    </p>
                    {regulatoryData?.metrics?.f2f?.overdue > 0 && (
                      <Badge className="mt-2 bg-red-100 text-red-700">RECERT AT RISK</Badge>
                    )}
                  </div>

                  {/* POC */}
                  <div className={`p-4 rounded-lg border ${
                    regulatoryData?.metrics?.poc?.overdue > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-5 w-5" />
                      <h4 className="font-semibold">Plan of Care Updates</h4>
                    </div>
                    <p className="text-2xl font-bold">{regulatoryData?.metrics?.poc?.compliance_pct || 100}%</p>
                    <p className="text-sm text-slate-600">
                      {regulatoryData?.metrics?.poc?.overdue || 0} overdue of {regulatoryData?.metrics?.poc?.total || 0}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {regulatoryData?.metrics?.poc?.description}
                    </p>
                  </div>

                  {/* IDG */}
                  <div className="p-4 rounded-lg border bg-slate-50 border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5" />
                      <h4 className="font-semibold">IDG Attendance</h4>
                    </div>
                    <p className="text-2xl font-bold">{regulatoryData?.metrics?.idg?.documented || 0}</p>
                    <p className="text-sm text-slate-600">
                      {regulatoryData?.metrics?.idg?.meetings || 0} meetings, {regulatoryData?.metrics?.idg?.missing || 0} missing docs
                    </p>
                    <p className="text-xs text-slate-400 mt-1">IDG attendance properly documented</p>
                  </div>

                  {/* Volunteer */}
                  <div className={`p-4 rounded-lg border ${
                    (regulatoryData?.metrics?.volunteer?.compliance_pct || 0) < 100 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <HeartHandshake className="h-5 w-5" />
                      <h4 className="font-semibold">Volunteer Hours</h4>
                    </div>
                    <p className="text-2xl font-bold">
                      {regulatoryData?.metrics?.volunteer?.actual_hours || 0} / {regulatoryData?.metrics?.volunteer?.required_hours || 0}
                    </p>
                    <p className="text-sm text-slate-600">
                      {regulatoryData?.metrics?.volunteer?.compliance_pct || 0}% of required hours
                    </p>
                    <p className="text-xs text-slate-400 mt-1">5% of total patient care hours required</p>
                  </div>

                  {/* Bereavement */}
                  <div className={`p-4 rounded-lg border ${
                    (regulatoryData?.metrics?.bereavement?.overdue || 0) > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-5 w-5" />
                      <h4 className="font-semibold">Bereavement Contacts</h4>
                    </div>
                    <p className="text-2xl font-bold">
                      {regulatoryData?.metrics?.bereavement?.completed || 0} / {regulatoryData?.metrics?.bereavement?.due || 0}
                    </p>
                    <p className="text-sm text-slate-600">
                      {regulatoryData?.metrics?.bereavement?.overdue || 0} overdue
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Timely bereavement follow-up contacts</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Regulatory Alerts */}
            {regulatoryData?.alerts?.length > 0 && (
              <Card className="border-amber-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <Bell className="h-5 w-5" />
                    Regulatory Alerts ({regulatoryData.alert_count})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {regulatoryData.alerts.map((alert, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        alert.severity === 'critical' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex items-start gap-2">
                          {alert.severity === 'critical' ? (
                            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge className={alert.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                                {alert.type}
                              </Badge>
                              <span className="text-xs text-slate-400">{alert.date}</span>
                            </div>
                            <p className="font-medium mt-1">{alert.message}</p>
                            <p className="text-sm text-slate-600 mt-1">
                              <ChevronRight className="h-3 w-3 inline" /> {alert.action}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {!regulatoryData && !regulatoryLoading && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Shield className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No regulatory compliance data available</p>
                  <p className="text-sm text-slate-400 mt-1">Add regulatory tracking entries to monitor CTI, F2F, and POC compliance</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Action Items Tab */}
        <TabsContent value="action">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Action Items This Week
              </CardTitle>
              <CardDescription>Items requiring immediate attention and follow-up (below 95% compliance)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {KPI_DEFINITIONS.map(kpi => {
                  let currentValue = 0;
                  if (kpiData) {
                    if (kpi.id === 'rncm_visits') currentValue = kpiData.rncm_missing_visits || 0;
                    else if (kpi.id === 'aide_visits') currentValue = kpiData.aide_missing_visits || 0;
                    else if (kpi.id === 'volunteer_visits') currentValue = kpiData.volunteer_missing_visits || 0;
                    else currentValue = kpiData[`${kpi.id}_missing`] || 0;
                  }
                  
                  if (currentValue === 0) return null;
                  
                  const totalCount = kpiData?.total_count || 0;
                  const percentage = calculatePercentage(currentValue, totalCount);
                  const followupValue = kpiData?.[`${kpi.id}_followup`] || kpiData?.[`${kpi.id}_followup_person`] || '';
                  const status = getKPIStatus(percentage);
                  const prediction = predictions?.[kpi.id];
                  
                  return (
                    <div key={kpi.id} className="flex items-center justify-between p-4 rounded-lg border bg-white">
                      <div className="flex items-center gap-4">
                        <div className={`h-12 w-12 rounded-full flex flex-col items-center justify-center ${status.color}`}>
                          <span className="text-white font-bold text-sm">{percentage}%</span>
                        </div>
                        <div>
                          <h4 className="font-medium">{kpi.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {currentValue} missing of {totalCount > 0 ? totalCount : '?'} total
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {prediction && (
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">Predicted next week:</p>
                            <p className={`font-medium ${prediction.predicted > currentValue ? 'text-red-600' : 'text-emerald-600'}`}>
                              {prediction.predicted} {prediction.predicted > currentValue ? '(increasing)' : '(improving)'}
                            </p>
                          </div>
                        )}
                        {followupValue ? (
                          <Badge variant="outline" className="gap-1">
                            <UserPlus className="h-3 w-3" />
                            {followupValue}
                          </Badge>
                        ) : (
                          <Badge variant="destructive">No assignee</Badge>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  );
                }).filter(Boolean)}
                
                {summary.action === 0 && summary.monitor === 0 && (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                    <p className="text-lg font-medium text-emerald-600">All KPIs Met!</p>
                    <p className="text-muted-foreground">No action items this week</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-[#00b0ab]" />
                    AI-Powered Insights & Predictions
                  </CardTitle>
                  <CardDescription>Analysis and recommendations based on your compliance data</CardDescription>
                </div>
                <div className="flex gap-2">
                  <AIExportDropdown token={token} reportType="insights" />
                  <Button onClick={fetchAIInsights} disabled={loadingAI}>
                    {loadingAI ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    {aiInsights ? 'Refresh' : 'Generate'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingAI ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
                </div>
              ) : aiInsights ? (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <AIContent content={aiInsights.insights} />
                </div>
              ) : (
                <div className="text-center py-12">
                  <Lightbulb className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Click "Generate" to get AI-powered insights</p>
                </div>
              )}

              {/* Predictions Section */}
              {predictions && Object.keys(predictions).length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-[#00b0ab]" />
                    Next Week Predictions
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(predictions).map(([kpiId, pred]) => {
                      const kpi = KPI_DEFINITIONS.find(k => k.id === kpiId);
                      if (!kpi) return null;
                      
                      return (
                        <div key={kpiId} className={`p-3 rounded-lg border ${pred.risk === 'high' ? 'bg-red-50 border-red-200' : pred.risk === 'medium' ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                          <p className="text-sm font-medium">{kpi.name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-muted-foreground">Current: {pred.current}</span>
                            <ArrowRight className="h-3 w-3" />
                            <span className={`text-sm font-bold ${pred.predicted > pred.current ? 'text-red-600' : pred.predicted < pred.current ? 'text-emerald-600' : 'text-slate-600'}`}>
                              Predicted: {pred.predicted}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab - Audit History Comparison */}
        <TabsContent value="trends">
          <div className="space-y-6">
            {/* Overall Compliance Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#00b0ab]" />
                  Overall Compliance Score Trend
                </CardTitle>
                <CardDescription>Week-over-week compliance rating comparison</CardDescription>
              </CardHeader>
              <CardContent>
                {weeklyHistory.length > 1 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={[...weeklyHistory].reverse().slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="report_date" 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(val) => {
                          const d = new Date(val);
                          return `${d.getMonth()+1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip 
                        formatter={(value) => [`${value}%`, 'Compliance']}
                        labelFormatter={(label) => `Week of ${label}`}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="overall_compliance_rating" 
                        name="Compliance %" 
                        stroke="#6366f1" 
                        strokeWidth={3}
                        dot={{ fill: '#6366f1', strokeWidth: 2, r: 5 }}
                        activeDot={{ r: 8, fill: '#4f46e5' }}
                      />
                      {/* Target line at 95% */}
                      <Line 
                        type="monotone"
                        dataKey={() => 95}
                        name="Target (95%)"
                        stroke="#10b981"
                        strokeDasharray="5 5"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Need at least 2 weeks of data to show trend</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Week-over-Week Comparison Cards */}
            {weeklyHistory.length >= 2 && (
              <Card className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ArrowRight className="h-5 w-5 text-blue-500" />
                    Week-over-Week Comparison
                  </CardTitle>
                  <CardDescription>
                    Comparing {weeklyHistory[1]?.week_label || weeklyHistory[1]?.report_date} vs {weeklyHistory[0]?.week_label || weeklyHistory[0]?.report_date}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {[
                      { label: 'Overall', key: 'overall_compliance_rating', suffix: '%', isPercent: true },
                      { label: 'RNCM Missing', key: 'rncm_missing_visits' },
                      { label: 'Aide Missing', key: 'aide_missing_visits' },
                      { label: 'Aide Supervisory', key: 'aide_supervisory_missing' },
                      { label: 'Initial POC', key: 'initial_poc_missing' },
                      { label: 'IDG Assessment', key: 'idg_assessment_missing' },
                      { label: 'Scheduling', key: 'scheduling_visits_missing' },
                      { label: 'Volunteer', key: 'volunteer_missing_visits' },
                      { label: 'Triage Risk', key: 'triage_risk_missing' },
                      { label: 'DME Orders', key: 'dme_orders_missing' },
                    ].map(metric => {
                      const current = weeklyHistory[0]?.[metric.key] || 0;
                      const previous = weeklyHistory[1]?.[metric.key] || 0;
                      const diff = current - previous;
                      const improved = metric.isPercent ? diff > 0 : diff < 0;
                      const worsened = metric.isPercent ? diff < 0 : diff > 0;
                      
                      return (
                        <div key={metric.key} className="bg-white dark:bg-slate-800 rounded-lg p-3 border">
                          <p className="text-xs text-muted-foreground mb-1">{metric.label}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xl font-bold">
                              {current}{metric.suffix || ''}
                            </span>
                            {diff !== 0 && (
                              <div className={`flex items-center text-xs px-1.5 py-0.5 rounded ${
                                improved ? 'bg-green-100 text-green-700' : worsened ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {improved ? <TrendingDown className="h-3 w-3 mr-0.5" /> : worsened ? <TrendingUp className="h-3 w-3 mr-0.5" /> : null}
                                {diff > 0 ? '+' : ''}{diff}{metric.suffix || ''}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Previous: {previous}{metric.suffix || ''}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Missing Visits Stacked Area Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                  Missing Visits Trend
                </CardTitle>
                <CardDescription>Historical view of missing visits by category</CardDescription>
              </CardHeader>
              <CardContent>
                {weeklyHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={[...weeklyHistory].reverse().slice(-12)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="report_date" 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(val) => {
                          const d = new Date(val);
                          return `${d.getMonth()+1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip 
                        labelFormatter={(label) => `Week of ${label}`}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="rncm_missing_visits" name="RNCM" stackId="1" stroke="#ef4444" fill="#fecaca" />
                      <Area type="monotone" dataKey="aide_missing_visits" name="Aide" stackId="1" stroke="#f59e0b" fill="#fef3c7" />
                      <Area type="monotone" dataKey="aide_supervisory_missing" name="Aide Supervisory" stackId="1" stroke="#8b5cf6" fill="#ede9fe" />
                      <Area type="monotone" dataKey="volunteer_missing_visits" name="Volunteer" stackId="1" stroke="#06b6d4" fill="#cffafe" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8">
                    <TrendingUp className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Enter weekly data to see trends</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Documentation Compliance Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Documentation & POC Compliance
                </CardTitle>
                <CardDescription>Plan of Care and documentation metrics over time</CardDescription>
              </CardHeader>
              <CardContent>
                {weeklyHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[...weeklyHistory].reverse().slice(-8)} barCategoryGap="15%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="report_date" 
                        tick={{ fontSize: 11 }}
                        tickFormatter={(val) => {
                          const d = new Date(val);
                          return `${d.getMonth()+1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip labelFormatter={(label) => `Week of ${label}`} />
                      <Legend />
                      <Bar dataKey="initial_poc_missing" name="Initial POC" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="updated_poc_missing" name="Updated POC" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="idg_assessment_missing" name="IDG Assessment" fill="#a855f7" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="scheduling_visits_missing" name="Scheduling" fill="#d946ef" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Enter weekly data to see trends</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Audit History Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-slate-500" />
                  Audit History
                </CardTitle>
                <CardDescription>Complete weekly audit records with compliance percentages</CardDescription>
              </CardHeader>
              <CardContent>
                {weeklyHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Week</TableHead>
                          <TableHead className="text-center">Total</TableHead>
                          <TableHead className="text-center">Overall %</TableHead>
                          <TableHead className="text-center">RNCM</TableHead>
                          <TableHead className="text-center">Aide</TableHead>
                          <TableHead className="text-center">Supervisory</TableHead>
                          <TableHead className="text-center">POC</TableHead>
                          <TableHead className="text-center">IDG</TableHead>
                          <TableHead className="text-center">Total Missing</TableHead>
                          <TableHead className="text-center w-20">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {weeklyHistory.slice(0, 12).map((week, idx) => {
                          const totalMissing = (week.rncm_missing_visits || 0) + 
                            (week.aide_missing_visits || 0) + 
                            (week.aide_supervisory_missing || 0) +
                            (week.volunteer_missing_visits || 0) +
                            (week.initial_poc_missing || 0) +
                            (week.idg_assessment_missing || 0);
                          const totalCount = week.total_count || 0;
                          
                          return (
                            <TableRow key={week.id || idx} className={idx === 0 ? 'bg-[#00b0ab]/10/50' : ''}>
                              <TableCell className="font-medium">
                                {week.week_label || week.report_date || (week.created_at ? new Date(week.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-')}
                                {idx === 0 && <Badge className="ml-2" variant="secondary">Latest</Badge>}
                              </TableCell>
                              <TableCell className="text-center text-muted-foreground">
                                {totalCount > 0 ? totalCount : '-'}
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant={week.overall_compliance_rating >= 95 ? 'default' : week.overall_compliance_rating >= 90 ? 'secondary' : 'destructive'}>
                                  {week.overall_compliance_rating || 0}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                {week.rncm_missing_visits || 0}
                                {totalCount > 0 && <span className="text-xs text-muted-foreground ml-1">({calculatePercentage(week.rncm_missing_visits || 0, totalCount)}%)</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                {week.aide_missing_visits || 0}
                                {totalCount > 0 && <span className="text-xs text-muted-foreground ml-1">({calculatePercentage(week.aide_missing_visits || 0, totalCount)}%)</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                {week.aide_supervisory_missing || 0}
                                {totalCount > 0 && <span className="text-xs text-muted-foreground ml-1">({calculatePercentage(week.aide_supervisory_missing || 0, totalCount)}%)</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                {week.initial_poc_missing || 0}
                                {totalCount > 0 && <span className="text-xs text-muted-foreground ml-1">({calculatePercentage(week.initial_poc_missing || 0, totalCount)}%)</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                {week.idg_assessment_missing || 0}
                                {totalCount > 0 && <span className="text-xs text-muted-foreground ml-1">({calculatePercentage(week.idg_assessment_missing || 0, totalCount)}%)</span>}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={totalMissing === 0 ? 'text-green-600 font-medium' : totalMissing <= 5 ? 'text-amber-600' : 'text-red-600 font-medium'}>
                                  {totalMissing}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => confirmDelete(week)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-audit-${week.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No audit history available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Data History Tab */}
        <TabsContent value="history" className="space-y-6">
          <SectionHeader 
            icon={Clock} 
            title="Compliance Data History" 
            subtitle="View timeline of compliance data entries and audit logs"
          />
          <DataHistoryPanel 
            category="compliance" 
            title="Compliance Data" 
            defaultExpanded={true}
            isAdmin={user?.role === 'admin' || user?.role === 'super_admin'}
          />
        </TabsContent>
      </Tabs>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Compliance Data</DialogTitle>
            <DialogDescription>
              Paste your compliance report data. The system will automatically extract KPI values.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Paste data here...
Example:
Overall: 94%
RNCM: 2
Aide: 3
Initial POC: 1
Scheduling: 0"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={10}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={importing}>
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Audit Entry
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this audit entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="py-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="font-medium">{deleteTarget.week_label || deleteTarget.report_date || (deleteTarget.created_at ? new Date(deleteTarget.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Entry')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Overall Compliance: {deleteTarget.overall_compliance_rating || 0}%
                </p>
                {deleteTarget.total_count > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Total Count: {deleteTarget.total_count}
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAudit} 
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
